/**
 * A2UI Data Model Store
 * Manages the data model for A2UI surfaces with support for data binding
 * Based on Google A2UI Protocol v0.8
 */

class DataModelStore {
  constructor() {
    this.surfaces = new Map(); // surfaceId -> data model
    this.listeners = new Map(); // surfaceId -> Set of callbacks
  }

  /**
   * Get or create a surface's data model
   */
  getSurfaceModel(surfaceId) {
    if (!this.surfaces.has(surfaceId)) {
      this.surfaces.set(surfaceId, {});
    }
    return this.surfaces.get(surfaceId);
  }

  /**
   * Process a dataModelUpdate message
   * @param {Object} update - The dataModelUpdate message
   */
  processDataModelUpdate(update) {
    const { surfaceId = 'default', path, contents } = update;
    const model = this.getSurfaceModel(surfaceId);
    
    // Convert adjacency list contents to nested object
    const data = this.parseContents(contents);
    
    if (path) {
      // Update at specific path
      this.setAtPath(model, path, data);
    } else {
      // Merge with root
      Object.assign(model, data);
    }
    
    this.surfaces.set(surfaceId, model);
    this.notifyListeners(surfaceId);
  }

  /**
   * Parse A2UI adjacency list contents into nested object
   */
  parseContents(contents) {
    if (!contents || !Array.isArray(contents)) return {};
    
    const result = {};
    for (const entry of contents) {
      const { key } = entry;
      if (entry.valueString !== undefined) {
        result[key] = entry.valueString;
      } else if (entry.valueNumber !== undefined) {
        result[key] = entry.valueNumber;
      } else if (entry.valueBoolean !== undefined) {
        result[key] = entry.valueBoolean;
      } else if (entry.valueArray !== undefined) {
        result[key] = entry.valueArray;
      } else if (entry.valueMap !== undefined) {
        result[key] = this.parseContents(entry.valueMap);
      }
    }
    return result;
  }

  /**
   * Set value at a path (e.g., "/user/name")
   */
  setAtPath(obj, path, value) {
    const parts = path.split('/').filter(p => p);
    let current = obj;
    
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) {
        current[parts[i]] = {};
      }
      current = current[parts[i]];
    }
    
    if (parts.length > 0) {
      const lastKey = parts[parts.length - 1];
      if (typeof value === 'object' && value !== null) {
        current[lastKey] = { ...current[lastKey], ...value };
      } else {
        current[lastKey] = value;
      }
    }
  }

  /**
   * Resolve a BoundValue against the data model
   * @param {Object} boundValue - The BoundValue object from A2UI
   * @param {string} surfaceId - The surface ID
   * @param {Object} itemContext - Optional context for template rendering
   * @returns {any} The resolved value
   */
  resolveBoundValue(boundValue, surfaceId, itemContext = null) {
    if (!boundValue) return undefined;
    
    // Handle literal values
    if (boundValue.literalString !== undefined) {
      // If both path and literal, initialize data model
      if (boundValue.path) {
        const model = this.getSurfaceModel(surfaceId);
        const currentValue = this.getAtPath(model, boundValue.path);
        if (currentValue === undefined) {
          this.setAtPath(model, boundValue.path, boundValue.literalString);
        }
        return this.getAtPath(model, boundValue.path);
      }
      return boundValue.literalString;
    }
    
    if (boundValue.literalNumber !== undefined) {
      if (boundValue.path) {
        const model = this.getSurfaceModel(surfaceId);
        const currentValue = this.getAtPath(model, boundValue.path);
        if (currentValue === undefined) {
          this.setAtPath(model, boundValue.path, boundValue.literalNumber);
        }
        return this.getAtPath(model, boundValue.path);
      }
      return boundValue.literalNumber;
    }
    
    if (boundValue.literalBoolean !== undefined) {
      if (boundValue.path) {
        const model = this.getSurfaceModel(surfaceId);
        const currentValue = this.getAtPath(model, boundValue.path);
        if (currentValue === undefined) {
          this.setAtPath(model, boundValue.path, boundValue.literalBoolean);
        }
        return this.getAtPath(model, boundValue.path);
      }
      return boundValue.literalBoolean;
    }
    
    if (boundValue.literalArray !== undefined) {
      return boundValue.literalArray;
    }
    
    // Handle path binding
    if (boundValue.path) {
      // Check item context first (for template rendering)
      if (itemContext && boundValue.path.startsWith('./')) {
        const relativePath = boundValue.path.substring(2);
        return this.getAtPath(itemContext, relativePath);
      }
      
      const model = this.getSurfaceModel(surfaceId);
      return this.getAtPath(model, boundValue.path);
    }
    
    return undefined;
  }

  /**
   * Get value at a path
   */
  getAtPath(obj, path) {
    if (!path) return obj;
    const parts = path.split('/').filter(p => p);
    let current = obj;
    
    for (const part of parts) {
      if (current === undefined || current === null) return undefined;
      current = current[part];
    }
    
    return current;
  }

  /**
   * Update a value at a path (for form inputs)
   */
  updateValue(surfaceId, path, value) {
    const model = this.getSurfaceModel(surfaceId);
    this.setAtPath(model, path, value);
    this.notifyListeners(surfaceId);
  }

  /**
   * Subscribe to changes for a surface
   */
  subscribe(surfaceId, callback) {
    if (!this.listeners.has(surfaceId)) {
      this.listeners.set(surfaceId, new Set());
    }
    this.listeners.get(surfaceId).add(callback);
    
    return () => {
      this.listeners.get(surfaceId)?.delete(callback);
    };
  }

  /**
   * Notify all listeners for a surface
   */
  notifyListeners(surfaceId) {
    const callbacks = this.listeners.get(surfaceId);
    if (callbacks) {
      callbacks.forEach(cb => cb(this.getSurfaceModel(surfaceId)));
    }
  }

  /**
   * Delete a surface's data model
   */
  deleteSurface(surfaceId) {
    this.surfaces.delete(surfaceId);
    this.listeners.delete(surfaceId);
  }

  /**
   * Clear all data
   */
  clear() {
    this.surfaces.clear();
    this.listeners.clear();
  }
}

// Export singleton instance
export const dataModelStore = new DataModelStore();
export default DataModelStore;
